import React, { Component } from 'react'
import PropTypes from 'prop-types'
import content from 'pp-react-l10n'
import { css, before } from 'glamor'
import { Tabs, TabPanel } from '@paypalcorp/pp-react'

import { routeTo } from '../../../utils/routeHelper'
import { connect } from 'transfer/utils/p2p-connect'
import { getNavHeight } from 'transfer/utils/preview-utils'

import glamorous from 'glamorous'
import glamorousLink from '../../../../lib/glamorous-link'

import {
  SIZE_SMALL,
  smallAndSmaller,
  biggerThanSmall,
} from '../../../../lib/styles/media-queries'

const i18n = content('transfer/page/recipient')

const NavBar = glamorous.div(
  {
    margin: '0 auto',
    backgroundColor: 'white',
    textAlign: 'center',
    borderBottom: '1px solid #cbd2d6',
    [smallAndSmaller]: {
      position: 'unset',
    },
    position: 'fixed',
    zIndex: 2,
    display: 'block',
    width: '100%',
    left: 0,
  },
  ({ navHeight }) => ({ top: navHeight })
)

const NavLink = glamorousLink({
  display: 'inline-block',
  padding: '20px 22px',
  textDecoration: 'none',
  width: 120,
  textAlign: 'center',
  fontSize: '0.875rem',
  fontFamily: "'Paypal-Sans-Big', sans-serif",
  margin: '0 25px',

  [smallAndSmaller]: {
    width: 75,
    paddingTop: '15px',
    paddingBottom: '15px',
    paddingLeft: '0px',
    paddingRight: '0px',
    margin: '0 6px',
  },

  ':hover, :focus': {
    cursor: 'pointer',
    textDecoration: 'none',
  },

  '> a:hover': {
    cursor: 'pointer',
    textDecoration: 'none',
  },

  ['.active']: {
    color: '#0070ba',
    borderBottom: '2px solid #0070ba',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    [smallAndSmaller]: {
      borderBottom: '3px solid #0070ba',
    },
  },

  ['.nonActive']: {
    color: '#2c2e2f',
    textDecoration: 'none',
  },
})

const Content = glamorous.div(
  {
    marginTop: '20px',
    marginBottom: '5rem',
    position: 'relative',
  },
  ({ navHeight }) => ({
    top:
      navHeight === 96
        ? navHeight - 34 // Personal Account Header
        : navHeight - 67, // Business Account Header
    [smallAndSmaller]: {
      top: '-36px',
    },
  })
)

class Tab extends Component {
  displayName: 'Tabs'

  state = {
    navHeight: 72,
  }

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    selected: PropTypes.number.isRequired,
    panes: PropTypes.array,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.element),
      PropTypes.element,
    ]),
  }

  mediaQueryListener = () => {
    this.setNavHeight()
  }

  componentDidMount = () => {
    this.setNavHeight() // Set nav height on mount

    // The merchant team dynamically changes merchant height depending
    // on the experience the user is in. They will fire this event on change.
    document.addEventListener('merchantHeaderMarginUpdate', this.setNavHeight)

    try {
      this.mediaQueryMatcher = window.matchMedia(`(max-width: ${SIZE_SMALL}px)`)
      if (this.mediaQueryMatcher.addEventListener) {
        this.mediaQueryMatcher.addEventListener(
          'change',
          this.mediaQueryListener
        )
      } else {
        this.mediaQueryMatcher.addListener(this.mediaQueryListener)
      }
    } catch (e) {
      // ignore old browsers that support neither match media interface
    }
  }

  componentWillUnmount = () => {
    document.removeEventListener(
      'merchantHeaderMarginUpdate',
      this.setNavHeight
    )

    try {
      if (this.mediaQueryMatcher.removeEventListener) {
        this.mediaQueryMatcher.removeEventListener(
          'change',
          this.mediaQueryListener
        )
      } else {
        this.mediaQueryMatcher.removeListener(this.mediaQueryListener)
      }
    } catch (e) {
      // ignore old browsers that support neither match media interface
    }
  }

  // Only dynamically change for merchant accounts
  setNavHeight = () => {
    this.setState({ navHeight: getNavHeight() })
  }

  _renderTitles = () => {
    const { navHeight } = this.state
    function labels(child, index) {
      const activeClass = this.props.selected === index ? 'active' : 'nonActive'
      const current = this.props.children[this.props.selected]
      return (
        <NavLink
          to={child.props.path}
          tabindex="0"
          key={index}
          onClick={e => {
            e.preventDefault()
            if (typeof child.props.onClickAction === 'function') {
              child.props.onClickAction(this.props.dispatch)
            }
            routeTo(child.props.path)
          }}
          onMouseOver={() => {
            // if child is a lazy loading component create from react loadable
            // preload the component on mouse over
            if (child.props.comp && child.props.comp.preload) {
              child.props.comp.preload()
            }
          }}
          onFocus={() => {
            // if child is a lazy loading component create from react loadable
            // preload the component on mouse over
            if (child.props.comp && child.props.comp.preload) {
              child.props.comp.preload()
            }
          }}
          className={activeClass}
          data-nemo={child.props.datanemo}
          data-pagename={current.props.analytics.pagegroup}
          data-pagename2={current.props.analytics.pagename}
          data-nextpagename={child.props.analytics.pagename}
          data-nextpagegroup={child.props.analytics.pagegroup}
          data-extra={JSON.stringify(child.props.analytics.extra)}
        >
          {child.props.label}
        </NavLink>
      )
    }
    return (
      <NavBar navHeight={navHeight}>
        {this.props.children.map(labels.bind(this))}
      </NavBar>
    )
  }

  _renderContent = () => {
    const { navHeight } = this.state
    return (
      <Content navHeight={navHeight}>
        {this.props.children[this.props.selected]}
      </Content>
    )
  }

  render() {
    return (
      <div>
        <Tabs
          name="secondary-nav-tabs"
          className={css(
            {
              position: 'fixed',
              top: this.state.navHeight,
              zIndex: 2,
              ' nav': {
                boxShadow: 'none',
                [biggerThanSmall]: {
                  margin: 0,
                },
              }, // TODO move to nav specific class
              [smallAndSmaller]: {
                position: 'relative',
                top: 0,
                backgroundColor: 'white',
                boxShadow:
                  '0rem 0rem 0.5rem rgb(0 0 0 / 4%), 0rem 0.125rem 1.125rem rgb(0 0 0 / 10%)',
              },
            },
            before({
              content: '""',
              position: 'fixed',
              top: this.state.navHeight,
              left: 0,
              backgroundColor: 'white',
              width: 999999999,
              height: '6rem',
              boxShadow:
                '0rem 0rem 0.5rem rgb(0 0 0 / 4%), 0rem 0.125rem 1.125rem rgb(0 0 0 / 10%)',
              [smallAndSmaller]: {
                display: 'none',
              },
            })
          ).toString()}
          selectedTabIdx={this.props.selected}
          onClick={(e, index) => {
            e.preventDefault()
            const pane = this.props.panes[index]
            if (typeof pane.onClickAction === 'function') {
              pane.onClickAction(this.props.dispatch)
            }
            routeTo(pane.path)
          }}
          tabs={this.props.panes.map((pane, index) => ({
            label: pane.label,
            panelId: `secondary-nav-tab-${index}`,
            onMouseOver: () => {
              if (pane.comp.preload) {
                pane.comp.preload()
              }
            },
            onFocus: () => {
              if (pane.comp.preload) {
                pane.comp.preload()
              }
            },
            'data-nemo': pane.datanemo,
            'data-pagename': pane.analytics.pagegroup,
            'data-pagename2': pane.analytics.pagename,
            'data-nextpagename': pane.analytics.pagename,
            'data-nextpagegroup': pane.analytics.pagegroup,
            'data-extra': JSON.stringify(pane.analytics.extra),
          }))}
          scrollLeftLabel={i18n('secondaryNav.scrollLeft')}
          scrollRightLabel={i18n('secondaryNav.scrollRight')}
        >
          {this.props.panes.map((pane, index) => (
            <TabPanel
              key={pane.type.feature}
              id={`secondary-nav-tab-${index}`}
              aria-hidden="true"
              tabIndex={-1}
            />
          ))}
        </Tabs>
        {this._renderContent()}
      </div>
    )
  }
}

export default connect()(Tab)
